import { type FunctionComponent } from 'react';

import { Icon, Loader, Popover, Text } from '@kanda-libs/ks-design-library';

import ActionRequired from 'components/ActionRequired';

import OmniSignDocumentsFormBasePageActionBar from './ActionBar';
import OmniSignDocumentsFormBasePageDocument from './Document';
import OmniSignDocumentsFormBasePageSignDocument from './SignDocument';
import { BasePage } from './constants';
import useOmniSignDocumentsFormBasePage from './useOmniSignDocumentsFormBasePage';

interface OmniSignDocumentsFormBasePageProps {
  page: BasePage;
  isSubmitting: boolean;
}

const OmniSignDocumentsFormBasePage: FunctionComponent<
  OmniSignDocumentsFormBasePageProps
> = ({ page, isSubmitting }) => {
  const { document, scrollRef, title, description } =
    useOmniSignDocumentsFormBasePage(page);
  return (
    <div className="flex flex-col w-full">
      <div ref={scrollRef} className="absolute top-0" />
      <div className="flex flex-row px-4 py-5 mb-4 bg-purple-50 border border-purple-300 rounded-lg">
        <Icon
          icon="info"
          stroke="purple-300"
          size={16}
          className="mt-px mb-auto mr-3"
        />
        <Text
          className="text-purple-300 text-12-18-em"
          text="Scroll to the bottom of this page to sign this document"
        />
      </div>
      <ActionRequired hiddenOnMobile />
      <Text
        text={title}
        className="mb-6 text-16-20-em md:text-20-24-em text-neutral-900"
        skeletonProps={{
          width: 200,
        }}
      />
      <Text
        text={description}
        className="mb-6 text-14-22 text-neutral-600"
        skeletonProps={{
          width: '100%',
          count: 2,
        }}
      />
      <OmniSignDocumentsFormBasePageActionBar document={document} />
      <Popover.Sticky
        content={<OmniSignDocumentsFormBasePageDocument document={document} />}
      >
        <>
          <div className="mb-3">
            <Text text="View Only" className="text-12-18-em text-neutral-900" />
          </div>
          <Text
            text="To accept this agreement, scroll to the bottom of this page and tick the checkbox. You do not need to fill in any more details"
            className="text-12-18 text-neutral-600"
          />
        </>
      </Popover.Sticky>
      <OmniSignDocumentsFormBasePageSignDocument
        scrollRef={scrollRef}
        page={page}
        isSubmitting={isSubmitting}
      />
      <Loader isLoading={isSubmitting} />
    </div>
  );
};

export default OmniSignDocumentsFormBasePage;
